import { formatDate as formatDateShared, formatCurrency as formatCurrencyShared } from '../../../utils/formatUtils';

/**
 * Formatea una fecha desde formato de base de datos a DD/MM/YYYY
 * @param {string} dateStr - Fecha en formato YYYY-MM-DD HH:mm:ss o YYYY-MM-DD
 * @returns {string} - Fecha formateada DD/MM/YYYY
 */
export const formatDate = (dateStr) => {
  return formatDateShared(dateStr, { fallback: '00/00/0000' });
};

/**
 * Formatea un monto como moneda argentina
 * @param {number} amount - Monto a formatear
 * @returns {string} - Monto formateado
 */
export const formatCurrency = (amount) => formatCurrencyShared(amount, { fallback: '$ 0,00' });

/**
 * Normaliza un valor numérico
 * @param {any} value - Valor a normalizar
 * @returns {number} - Valor normalizado
 */
export const normalizeNumericValue = (value) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Valida si una fecha es válida
 * @param {string} dateString - Fecha en string
 * @returns {boolean} - True si es válida
 */
export const isValidDate = (dateString) => {
  if (!dateString || dateString === 'none') return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Calcula el total de regularizaciones de un contrato
 * @param {Array} coupons - Lista de cupones regularizados
 * @returns {Object} - Totales calculados
 */
export const calculateRegularizationTotals = (coupons) => {
  if (!Array.isArray(coupons)) {
    return {
      totalAmount: 0,
      totalCoupons: 0,
      firstRegularizations: 0,
      secondRegularizations: 0,
    };
  }

  return coupons.reduce((totals, coupon) => {
    const firstAmount = normalizeNumericValue(coupon.regulariza1_amount);
    const secondAmount = normalizeNumericValue(coupon.regulariza2_amount);
    
    return {
      totalAmount: totals.totalAmount + firstAmount + secondAmount,
      totalCoupons: totals.totalCoupons + 1,
      firstRegularizations: firstAmount > 0 ? totals.firstRegularizations + 1 : totals.firstRegularizations,
      secondRegularizations: secondAmount > 0 ? totals.secondRegularizations + 1 : totals.secondRegularizations,
    };
  }, {
    totalAmount: 0,
    totalCoupons: 0,
    firstRegularizations: 0,
    secondRegularizations: 0,
  });
};

/**
 * Filtra cupones por estado de regularización
 * @param {Array} coupons - Lista de cupones
 * @param {string} status - Estado a filtrar ('pending', 'partial', 'complete')
 * @returns {Array} - Cupones filtrados
 */
export const filterCouponsByRegularizationStatus = (coupons, status) => {
  if (!Array.isArray(coupons)) return [];

  return coupons.filter(coupon => {
    const hasFirstRegularization = normalizeNumericValue(coupon.regulariza1_amount) > 0;
    const hasSecondRegularization = normalizeNumericValue(coupon.regulariza2_amount) > 0;

    switch (status) {
      case 'pending':
        return !hasFirstRegularization && !hasSecondRegularization;
      case 'partial':
        return hasFirstRegularization && !hasSecondRegularization;
      case 'complete':
        return hasFirstRegularization && hasSecondRegularization;
      default:
        return true;
    }
  });
};

/**
 * Genera un resumen de regularizaciones por contrato
 * @param {Object} contractData - Datos del contrato
 * @returns {Object} - Resumen de regularizaciones
 */
export const generateRegularizationSummary = (contractData) => {
  if (!contractData || !contractData.coupons) {
    return {
      contract_number: contractData?.header?.contract_number || 'N/A',
      subscriber: contractData?.header?.subscriber || 'N/A',
      summary: 'No hay datos disponibles',
    };
  }

  const totals = calculateRegularizationTotals(contractData.coupons);
  const pendingCoupons = filterCouponsByRegularizationStatus(contractData.coupons, 'pending');
  const partialCoupons = filterCouponsByRegularizationStatus(contractData.coupons, 'partial');
  const completeCoupons = filterCouponsByRegularizationStatus(contractData.coupons, 'complete');

  return {
    contract_number: contractData.header.contract_number,
    subscriber: contractData.header.subscriber,
    totals,
    status: {
      pending: pendingCoupons.length,
      partial: partialCoupons.length,
      complete: completeCoupons.length,
    },
    summary: `${totals.totalCoupons} cupones, ${formatCurrency(totals.totalAmount)} regularizado`,
  };
};

/**
 * Transforma los datos de la API para su visualización en la tabla
 * @param {Array} apiData - Datos desde la API
 * @returns {Array} - Datos transformados para la tabla
 */
export const transformRegularizedPlansData = (apiData) => {
  if (!Array.isArray(apiData)) return [];

  return apiData.map(item => ({
    ...item,
    formattedTotal: formatCurrency(item.header?.total),
    summary: generateRegularizationSummary(item),
  }));
};
